import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Anchor, Button, Menu, UnstyledButton } from "@mantine/core"
import { IconDotsVertical } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { identityInkColor } from "../../../lib/speaker-colors"
import { fmtTimestamp } from "../../MeetingSummary/helpers"
import { SpeakerRosterPicker } from "../../SpeakerRosterPicker"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

export function SpeakerRow({
  speaker,
  displayLabel,
  statusText,
  confirmedByYou,
  voiceSaved,
  fresh = false,
  focused = false,
  evidenceLine,
  runnerUpLine,
  conflictLine,
  conflictPeerLabel,
  duplicateLine = null,
  mergeOptions,
  sampleSeconds,
  showSample = false,
  pickerOpen = false,
  pickerQuery = "",
  pickerPeople = [],
  pickerLoading = false,
  pickerSuggested = null,
  pickerUsing = false,
  pickerError = null,
  confirmPending = false,
  rejectPending = false,
  mergePending = false,
  error = null,
  onPlaySample,
  onConfirm,
  onReject,
  onPickSomeoneElse,
  onNameSpeaker,
  onEdit,
  onMerge,
  onMergeIntoConflict,
  onPickerQueryChange,
  onPickerCommit,
  onPickerCancel,
  rowRef,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const unnamed = speaker.status === "unidentified" || speaker.status === "suggested"
  const name = displayLabel
  const suggestion = speaker.status === "suggested" ? speaker.suggestion : undefined
  const talkPercent = speaker.talkPercent
  const firstSpoke =
    speaker.firstSpokeSeconds != null ? i18n._(msg`first spoke ${fmtTimestamp(speaker.firstSpokeSeconds)}`) : null
  const showNotThem =
    Boolean(onReject) &&
    (speaker.status === "named" || speaker.status === "confirmed" || speaker.status === "suggested")
  const showMerge = mergeOptions.length > 0 && Boolean(onMerge)
  const showMenu = !pickerOpen && (showNotThem || showMerge)

  return (
    <div
      ref={rowRef}
      className={classes.row}
      data-focused={focused ? "true" : "false"}
      data-fresh={fresh ? "true" : "false"}
      data-testid={`who-spoke-row-${speaker.label ?? speaker.displayName}`}
    >
      <span
        className={`${classes.avatar}${speaker.status === "unidentified" ? ` ${classes.avatarUnnamed}` : ""}`}
        style={
          {
            background: speaker.resolvedColor,
            color: identityInkColor(speaker.resolvedColor),
            "--row-speaker": speaker.resolvedColor,
          } as React.CSSProperties
        }
        aria-hidden
      >
        {unnamed ? "?" : speaker.initials}
      </span>

      <div className={classes.body}>
        <div className={classes.nameLine}>
          {onEdit && !unnamed ? (
            <UnstyledButton
              className={classes.name}
              onClick={onEdit}
              aria-label={i18n._(msg`Edit profile ${name}`)}
              data-testid={`who-spoke-edit-${name}`}
            >
              {displayLabel}
            </UnstyledButton>
          ) : (
            <span className={unnamed ? `${classes.name} ${classes.nameUnnamed}` : classes.name}>{displayLabel}</span>
          )}
          <span className={classes.status}>
            {confirmedByYou ? (
              <>
                <span className={classes.statusOk}>
                  <Trans>✓ confirmed by you</Trans>
                </span>
                {voiceSaved ? (
                  <>
                    {" · "}
                    {speaker.speakerId ? (
                      <Anchor
                        size="xs"
                        renderRoot={(props) => (
                          <Link to="/speakers" search={{ voice: speaker.speakerId ?? undefined }} {...props} />
                        )}
                      >
                        <Trans>voice saved</Trans>
                      </Anchor>
                    ) : (
                      <Trans>voice saved</Trans>
                    )}
                  </>
                ) : null}
              </>
            ) : (
              statusText
            )}
          </span>
        </div>

        <div className={classes.stats}>
          {talkPercent != null ? (
            <>
              <span className={classes.share} aria-hidden>
                <span
                  className={classes.shareFill}
                  style={{ "--share-pct": `${talkPercent}%` } as React.CSSProperties}
                />
              </span>
              <span>
                <Trans>Talk share {talkPercent}%</Trans>
              </span>
            </>
          ) : null}
          {firstSpoke ? <span>{firstSpoke}</span> : null}
          {showSample && onPlaySample && sampleSeconds > 0 ? (
            <Button
              className={classes.play}
              size="compact-xs"
              variant="default"
              radius="xl"
              onClick={onPlaySample}
              data-testid="who-spoke-sample"
            >
              <Trans>▶ {sampleSeconds} s sample</Trans>
            </Button>
          ) : !showSample ? (
            <span>
              <Trans>No recording sample — name carefully.</Trans>
            </span>
          ) : (
            <span>
              <Trans>No timed clip — play the recording above.</Trans>
            </span>
          )}
        </div>

        {duplicateLine && speaker.status !== "unidentified" && speaker.status !== "suggested" ? (
          <div className={classes.conflict} role="status">
            {duplicateLine}
          </div>
        ) : null}

        {pickerOpen && onPickerQueryChange && onPickerCancel && onPickerCommit ? (
          <SpeakerRosterPicker
            query={pickerQuery}
            onQueryChange={onPickerQueryChange}
            people={pickerPeople}
            loading={pickerLoading}
            suggested={pickerSuggested}
            onCommit={onPickerCommit}
            onCancel={onPickerCancel}
            using={pickerUsing}
            error={pickerError}
          />
        ) : suggestion ? (
          <div className={classes.suggest}>
            <div className={classes.suggestTop}>
              <span className={classes.cand}>{suggestion.name}?</span>
              <span className={classes.conf}>
                <Trans>{Math.round(suggestion.score * 100)}% confidence</Trans>
              </span>
            </div>
            {evidenceLine.length > 0 ? <div className={classes.why}>{evidenceLine}</div> : null}
            {runnerUpLine ? <div className={classes.alt}>{runnerUpLine}</div> : null}
            {conflictLine ? <div className={classes.conflict}>{conflictLine}</div> : null}
            <div className={classes.btns}>
              <Button size="compact-sm" onClick={onConfirm} loading={confirmPending} disabled={rejectPending}>
                {i18n._(msg`✓ Confirm ${suggestion.name}`)}
              </Button>
              <Button
                size="compact-sm"
                variant="subtle"
                onClick={onPickSomeoneElse}
                disabled={confirmPending || rejectPending}
                data-testid="who-spoke-open-name"
              >
                <Trans>Pick someone else</Trans>
              </Button>
            </div>
          </div>
        ) : speaker.status === "unidentified" ? (
          <div className={classes.suggest} data-tone={conflictLine ? "warning" : undefined}>
            {conflictLine ? <div className={classes.why}>{conflictLine}</div> : null}
            {!conflictLine ? (
              <div className={classes.why}>
                {showSample && sampleSeconds > 0 ? (
                  <Trans>No suggested name — play the sample and name them yourself.</Trans>
                ) : (
                  <Trans>No suggested name — name them yourself.</Trans>
                )}
              </div>
            ) : null}
            <div className={classes.btns}>
              <Button
                size="compact-sm"
                variant={conflictLine ? "default" : "filled"}
                onClick={onNameSpeaker}
                data-testid="who-spoke-open-name"
              >
                <Trans>Name this speaker</Trans>
              </Button>
              {conflictPeerLabel && onMergeIntoConflict ? (
                <Button size="compact-sm" variant="subtle" onClick={onMergeIntoConflict} loading={mergePending}>
                  {i18n._(msg`Merge into ${conflictPeerLabel}`)}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className={classes.actions}>
        {showMenu ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" aria-label={i18n._(msg`Speaker actions`)} data-testid="who-spoke-row-menu">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {showNotThem ? (
                <Menu.Item onClick={onReject} disabled={rejectPending || confirmPending}>
                  <Trans>Not them</Trans>
                </Menu.Item>
              ) : null}
              {showMerge && onMerge ? (
                <>
                  {showNotThem ? <Menu.Divider /> : null}
                  <Menu.Label>
                    <Trans>Merge with…</Trans>
                  </Menu.Label>
                  {mergeOptions.map((opt) => (
                    <Menu.Item key={opt.displayName} onClick={() => onMerge(opt)} disabled={mergePending}>
                      {opt.displayName}
                    </Menu.Item>
                  ))}
                </>
              ) : null}
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </div>
    </div>
  )
}
